import * as I from 'immutable';
import queryString from 'query-string';
import React from 'react';

import {ApiFeature} from 'app/modules/Remote/Feature';
import {ApiOrganization} from 'app/modules/Remote/Organization';
import {ApiProject} from 'app/modules/Remote/Project';
import {DEFAULT_FORM_STATE, FormState} from 'app/pages/SignUp/types';
import {
  LENS_ACCESS_SUBSCRIPTIONS_TYPE,
  LENS_ACCESS_SUBSCRIPTION_SIGNUP_OPTIONS,
  LENS_APP_LEGACY_URL,
  LENS_APP_PROD_URL,
} from 'app/utils/constants';

import * as historyUtils from './historyUtils';

export const UUID_PREFIX_REGEXP = /^[0-9a-f]{8}$/i;
export const UUID_REGEXP =
  /^([0-9a-f]{8})-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export const SCENE_DATE_QUERY_PARAM = 'sceneDate';
export const SCENE_LAYER_KEY_QUERY_PARAM = 'sceneLayerKey';
export const SCENE_URL_MIRRORING_STATE_KEY = 'sceneUrlMirroring';
export const SUBSET_FEATURE_BBOX_URL_PARAM = 'subsetFeatureBbox';
export const ORDERABLE_SCENE_URL_PARAMS = [
  SCENE_DATE_QUERY_PARAM,
  SCENE_LAYER_KEY_QUERY_PARAM,
  SUBSET_FEATURE_BBOX_URL_PARAM,
];

export type SceneUrlQueryParams = {
  [SCENE_DATE_QUERY_PARAM]: string;
  [SCENE_LAYER_KEY_QUERY_PARAM]?: string;
  [SUBSET_FEATURE_BBOX_URL_PARAM]?: string;
};
// Deep link to an agent explore run: selects the run in AgentExplorer and
// opens the Agent sidebar tab (see useTabbedSidebarState).
export const AGENT_RUN_ID_QUERY_PARAM = 'agentRunId';

// Router state on a navigation the agent chat makes, so the destination opens
// its Chat tab without rewriting the URL (see useTabbedSidebarState).
export const OPEN_CHAT_STATE_KEY = 'openChat';
export const OPEN_CHAT_STATE = {[OPEN_CHAT_STATE_KEY]: true};

// Router state on a note link followed from the agent chat, so the note opens
// in its notes pane, even when it is the note already focused (see
// useNoteUrlState).
export const OPEN_NOTE_STATE_KEY = 'openNote';
export const OPEN_NOTE_STATE = {[OPEN_NOTE_STATE_KEY]: true};

// Opens the destination's Chat tab when a URL from outside the app carries it,
// as a chat link copied before OPEN_CHAT_STATE does; consumed (removed from the
// URL) on arrival.
export const OPEN_CHAT_QUERY_PARAM = 'chat';

// Deep link to a saved report from the portfolio's Documents tab: opens the
// property's Reports tab and the report itself (see useReportExportPortal);
// consumed on arrival.
export const OPEN_REPORT_QUERY_PARAM = 'report';

// `table` and `map` are views of the Properties tab; `documents` and `shareLinks`
// are views of the Documents tab.
export type ProjectDashboardPage = 'table' | 'map' | 'documents' | 'shareLinks';

export function removeQueryParamFromSearch(search: string, key: string): string {
  const params = queryString.parse(search);
  if (!(key in params)) {
    return search;
  }
  delete params[key];
  const next = queryString.stringify(params);
  return next ? `?${next}` : '';
}

export function addAgentRunIdToUrl(url: string, runId: number): string {
  return queryString.stringifyUrl({
    url,
    query: {[AGENT_RUN_ID_QUERY_PARAM]: String(runId)},
  });
}

// One run of a run plan, where the results for every property it covers land.
export function agentWorkflowRunUrl(
  organizationId: string,
  workflowId: number | string,
  workflowRunId: number | string
): string {
  return `/${makeUuidPrefix(organizationId)}/agent/workflows/${workflowId}/runs/${workflowRunId}`;
}

// A link into the full-page chat, from the Automations page. `chat` opens a past chat and `new`
// starts a fresh one. `preset` offers its prompts, and `workflow` fills the input with a saved
// automation. All are consumed on arrival.
export type AgentChatParams = {
  chat?: string;
  new?: boolean;
  preset?: string;
  workflow?: number;
};

// Router state on a link into the chat, carrying a message for it to send. Kept out of the URL,
// which telemetry records and anyone can link to (see AgentChatPage).
export const CHAT_MESSAGE_STATE_KEY = 'chatMessage';

export function agentChatUrl(organizationId: string, params: AgentChatParams = {}): string {
  return queryString.stringifyUrl({
    url: `/${makeUuidPrefix(organizationId)}/chat`,
    query: {...params, new: params.new ? 1 : undefined},
  });
}

export function automationsUrl(organizationId: string): string {
  return `/${makeUuidPrefix(organizationId)}/automations`;
}

// The run selection is feature-scoped, so navigations away from the feature
// (see useChangeSelection) drop it rather than carry it along.
export function removeAgentRunIdFromSearch(search: string): string {
  return removeQueryParamFromSearch(search, AGENT_RUN_ID_QUERY_PARAM);
}

export const NOTE_URL_PARAM = 'note-';
export const NOTE_URL_PARAM_REGEX = new RegExp(`(${NOTE_URL_PARAM})(\\d*)`);

export function getStringParamFromQueryParams(
  params: queryString.ParsedQuery<string>,
  key: string
): string | undefined {
  const value = params[key];
  if (Array.isArray(value)) {
    return value[0] ?? undefined; // Take first if multiple values
  }
  return value ?? undefined; // Convert null to undefined
}

const PROD_ORIGINS = new Set([LENS_APP_PROD_URL, LENS_APP_LEGACY_URL]);

// Share links name the primary domain from either prod host, so links copied on the legacy
// domain don't keep it alive. A preview channel keeps its own host so the link opens its build.
export const makeAppUrl = (path: string | null) => {
  const origin = PROD_ORIGINS.has(window.location.origin)
    ? LENS_APP_PROD_URL
    : window.location.origin;
  return `${origin}${path ?? ''}`;
};

/*
 * Returns whether the current window url is a public lens url
 */
export const isPublicLensUrl = (): boolean => window.location.pathname.startsWith('/p/');

// Share-link pages carry the link's uuid in their path, and that uuid is the credential
// every API and tile request sends.
const SHARE_LINK_PATH_REGEXP = /^\/p\/(?:[rsw]\/)?([^/]+)/;

/**
 * The share link uuid the current page is authenticated as, or null when the page isn't a
 * share link. Map links live at `/p/<uuid>`, report links at `/p/r/<uuid>`, story links at
 * `/p/s/<uuid>` and widget links at `/p/w/<uuid>`.
 */
export const getShareLinkIdFromUrl = (): string | null => {
  const match = window.location.pathname.match(SHARE_LINK_PATH_REGEXP);
  return match ? match[1] : null;
};

export function featureMatchesParam(feature: I.ImmutableOf<ApiFeature>, param: string) {
  const id = feature.get('id');
  const lensId = feature.getIn(['properties', 'lensId']);

  if (param.match(/^\d+$/) && id === parseInt(param)) {
    return true;
  } else if (param.match(UUID_PREFIX_REGEXP) && lensId?.startsWith(param)) {
    return true;
  } else if (param.match(UUID_REGEXP) && lensId === param) {
    return true;
  } else {
    return false;
  }
}

export function makeUuidPrefix(uuid: string) {
  const match = uuid.match(UUID_REGEXP);

  if (match) {
    return match[1];
  } else {
    return uuid;
  }
}

export function makeProjectDashboardUrl(
  organization: I.ImmutableOf<ApiOrganization> | string,
  project: I.ImmutableOf<ApiProject> | string,
  page?: ProjectDashboardPage,
  featureLensIds?: (string | number | I.ImmutableOf<ApiFeature>)[],
  fragment?: string,
  options: {includeRoot: boolean; queryParams?: Record<string, string>} = {includeRoot: false}
) {
  const parts = [
    options.includeRoot ? historyUtils.ROOT_URL : '',
    makeUuidPrefix(typeof organization === 'string' ? organization : organization.get('id')),
    'projects',
    makeUuidPrefix(typeof project === 'string' ? project : project.get('id')),
  ];

  if (page) {
    parts.push(page);

    if (featureLensIds) {
      parts.push(
        featureLensIds
          .map((id) =>
            typeof id === 'object'
              ? makeUuidPrefix(id.getIn(['properties', 'lensId']) ?? id.get('id'))
              : makeUuidPrefix(id.toString())
          )
          .join(',')
      );
    }
  }

  const path = parts.join('/');
  const query = options.queryParams ? `?${queryString.stringify(options.queryParams)}` : '';
  const hash = fragment ? `#${fragment}` : '';
  return path + query + hash;
}

// A report presented as a map slideshow. The feature is the report's own
// property; `slide` (1-based) is a query param so a slide is bookmarkable.
export const STORY_ROUTE_PATH =
  '/:currentOrgIdPrefix/projects/:selectedProjectId/features/:featureId/stories/:reportId';
export const STORY_SLIDE_QUERY_PARAM = 'slide';

export function makeStoryUrl(
  organizationId: string,
  projectId: string,
  featureId: number,
  reportId: number,
  slide?: number
) {
  const path = `/${makeUuidPrefix(organizationId)}/projects/${makeUuidPrefix(
    projectId
  )}/features/${featureId}/stories/${reportId}`;
  return slide ? `${path}?${STORY_SLIDE_QUERY_PARAM}=${slide}` : path;
}

export function makeSavedReportUrl(
  organizationId: string,
  projectId: string,
  featureLensId: string,
  reportId: number
) {
  return makeProjectDashboardUrl(organizationId, projectId, 'map', [featureLensId], undefined, {
    includeRoot: false,
    queryParams: {[OPEN_REPORT_QUERY_PARAM]: String(reportId)},
  });
}

export function makeSceneUrlQueryParams(
  date: string,
  highResSourceId?: string,
  orderBboxString?: string
): SceneUrlQueryParams {
  const layerKey = highResSourceId ? `${highResSourceId}_high-res-truecolor` : undefined;
  const params = {
    [SCENE_DATE_QUERY_PARAM]: date,
  };
  if (layerKey) {
    params[SCENE_LAYER_KEY_QUERY_PARAM] = layerKey;
  }
  if (orderBboxString) {
    params[SUBSET_FEATURE_BBOX_URL_PARAM] = orderBboxString;
  }
  return params;
}

export function makeNoteUrlFragment(noteId) {
  return `${NOTE_URL_PARAM}${noteId}`;
}

export function makeHighResSceneUrl({
  organizationId,
  projectId,
  featureLensId,
  sourceId,
  date,
  orderBboxString,
  includeRoot = true,
}: {
  organizationId: string;
  projectId: string;
  featureLensId: string;
  sourceId: string;
  date: string;
  orderBboxString?: string;
  includeRoot?: boolean;
}): string {
  const queryParams = makeSceneUrlQueryParams(date, sourceId, orderBboxString);
  return makeProjectDashboardUrl(organizationId, projectId, 'map', [featureLensId], undefined, {
    includeRoot: includeRoot,
    queryParams,
  });
}

export const CurrentOrgIdPrefix = React.createContext<string | null>(null);
/* Hook to let components retrieve the current org id prefix. Must be rendered under CurrentOrgIdPrefix.Provider. */
export const useCurrentOrgIdPrefix = (): string => {
  const currentOrgIdPrefix = React.useContext(CurrentOrgIdPrefix);

  if (!currentOrgIdPrefix) {
    throw new Error(
      'This component must be rendered underneath a routeUtils.CurrentOrgIdPrefix.Provider'
    );
  }

  return currentOrgIdPrefix;
};

interface SignUpQueryParameters {
  subscriptionType?: LENS_ACCESS_SUBSCRIPTIONS_TYPE;
  accountEmail?: string;
  organizationName?: string;
  taxExempt?: string;
  subscriptionAddOns?: string | string[]; // If there is one add on this will be a string. If there are multiple this will be a list
  freeTrial?: string;
}
/**
 * Takes in signup query parameters and converts them into a FormState object
 */
export function parseSignUpQueryParams(params: SignUpQueryParameters): FormState {
  let subscriptionAddOns: string[];
  if (!params.subscriptionAddOns) {
    subscriptionAddOns = DEFAULT_FORM_STATE.subscriptionAddOns;
  } else if (params.subscriptionAddOns instanceof Array) {
    subscriptionAddOns = params.subscriptionAddOns;
  } else {
    subscriptionAddOns = [params.subscriptionAddOns];
  }

  //check if the user's URL subscriptionType is available in LENS_ACCESS_SUBSCRIPTION_SIGNUP_OPTIONS;
  //if it's not, check if their URL subscriptionType begins with a real subscriptionType and give them that
  //(if not, it will return undefined, and give them a default from the DEFAULT_FORM_STATE)
  const cleanedSubscriptionType: string | undefined =
    params.subscriptionType && !(params.subscriptionType in LENS_ACCESS_SUBSCRIPTION_SIGNUP_OPTIONS)
      ? Object.keys(LENS_ACCESS_SUBSCRIPTION_SIGNUP_OPTIONS).find((option) =>
          params.subscriptionType!.startsWith(option)
        )
      : params.subscriptionType;

  const defaultFormState: FormState = {
    ...DEFAULT_FORM_STATE,
    accessSubscriptionType:
      (cleanedSubscriptionType as LENS_ACCESS_SUBSCRIPTIONS_TYPE) ||
      params.subscriptionType ||
      DEFAULT_FORM_STATE.accessSubscriptionType, //give a default of plus_annual if subscription type couldn't be found
    accountEmail: params.accountEmail || DEFAULT_FORM_STATE.accountEmail,
    organizationName: params.organizationName || DEFAULT_FORM_STATE.organizationName,
    taxExempt: params.taxExempt?.toLowerCase() === 'true' || DEFAULT_FORM_STATE.taxExempt,
    freeTrial: params.freeTrial || DEFAULT_FORM_STATE.freeTrial,
    subscriptionAddOns,
  };
  return defaultFormState;
}

// Make it's own function so we can mock it out and spy on it in tests
export function setWindowLocationHref(href: string) {
  window.location.href = href;
}

// Signing out clears this origin's Firebase session first, which re-renders the app as logged
// out — and every redirect that sends a logged-out user to the login app's root would then
// race the handoff to its /signOut. Whichever navigation lands last wins, and at the root the
// app switcher hands a still-signed-in user straight back here off the referrer, root session
// untouched. So once a sign-out starts it owns the navigation: root redirects go through
// redirectToLoginApp and stand down, while the /signOut handoff itself navigates directly.
let signOutHandoffStarted = false;

export function startSignOutHandoff() {
  signOutHandoffStarted = true;
}

export function redirectToLoginApp(href: string) {
  if (signOutHandoffStarted) {
    return;
  }
  setWindowLocationHref(href);
}
